import React, { useState } from "react";
import type { PopupPhonePageContent } from "@/shared/types/popup-types";
import type { ResolvedStepDesign } from "@/shared/types/popup-design";
import { formatWhatsAppNumber, validateWhatsAppNumber } from "@/lib/popup-validation";
import { litButtonStyle, sanitizeInlineHtml, textCss } from "./engine";

interface PhonePageProps {
  content: PopupPhonePageContent;
  step: ResolvedStepDesign;
  /** Valid number submitted (already formatted). */
  onSubmit: (whatsappNumber: string) => void;
  /** Skip link: submit without a number. */
  onSkip: () => void;
  /** Static preview / builder: renders, never submits. */
  inert?: boolean;
}

/** The phone-number page: small print, one phone input, the CTA and an
 *  optional skip link. Styled inline from the design's inputs/button/footer
 *  elements so host stylesheets cannot restyle it. */
export const PhonePage: React.FC<PhonePageProps> = ({ content, step, onSubmit, onSkip, inert = false }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputs = step.elements.inputs;
  const btn = step.elements.button;
  const consent = String(content.consent_text || "").trim();
  const dismiss = String(content.dismiss_text || "").trim();
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inert) return;
    // Visitors type a bare number; the validator wants the + prefix.
    const raw = value.trim();
    const candidate = raw.startsWith("+") ? raw : raw ? "+" + raw : raw;
    const err = validateWhatsAppNumber(candidate);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    onSubmit(formatWhatsAppNumber(candidate));
  };
  const height = btn.customHeightPx > 0 ? btn.customHeightPx : 50;
  const lit = btn.finish === "lit" ? litButtonStyle(btn.background, btn.hoverBackground) : null;
  return (
    <form className="flowapt-phone-page" onSubmit={submit} noValidate style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
      {consent && (
        <p
          className="fp-rich"
          style={{ ...textCss("footer", step.elements.footer), fontSize: 11, lineHeight: 1.35, margin: "0 0 4px" }}
          dangerouslySetInnerHTML={{ __html: sanitizeInlineHtml(consent) }}
        />
      )}
      <div>
        <input
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder={content.placeholder || "Your phone number"}
          value={value}
          disabled={inert}
          maxLength={21}
          onChange={(e) => {
            setValue(e.target.value);
            if (error) setError(null);
          }}
          style={{
            width: "100%",
            boxSizing: "border-box",
            height,
            padding: "0 18px",
            fontSize: 16,
            fontFamily: "inherit",
            color: inputs.textColor,
            background: inputs.background,
            border: `${inputs.borderWidth ?? 1}px solid ${error ? "#dc2626" : inputs.borderColor}`,
            borderRadius: inputs.radius,
            outline: "none",
          }}
        />
        {error && (
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626", textAlign: "center" }}>{error}</p>
        )}
      </div>
      <button
        type="submit"
        disabled={inert}
        className={lit ? "fp-btn fp-btn-lit" : undefined}
        style={{
          width: "100%",
          height,
          border: "none",
          cursor: inert ? "default" : "pointer",
          ...(lit ? { backgroundColor: btn.background, ...lit } : { background: btn.background }),
          color: btn.textColor,
          borderRadius: btn.radius,
          fontSize: btn.fontSizePx > 0 ? btn.fontSizePx : 16,
          fontWeight: 600,
          fontFamily: "inherit",
          letterSpacing: btn.letterSpacing || undefined,
        }}
      >
        {content.button_text || "Continue"}
      </button>
      {dismiss && (
        <button
          type="button"
          disabled={inert}
          onClick={() => {
            if (!inert) onSkip();
          }}
          style={{
            background: "none",
            border: "none",
            padding: 4,
            cursor: inert ? "default" : "pointer",
            fontFamily: "inherit",
            fontSize: 14,
            textDecoration: "underline",
            color: step.elements.subheading.color || step.elements.heading.color,
          }}
        >
          {dismiss}
        </button>
      )}
    </form>
  );
};
